import React from 'react';
import { type Moment, type MomentInput } from 'moment';
import moment from 'moment-timezone';
import { withFormsy } from 'formsy-react';
import { useIntl } from 'react-intl';
import { IconButton, styled } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';

import BaseTextField, { type BaseTextFieldProps, type FieldProps } from './components/BaseTextField';
import { parseDate } from '../Utils';
import useSettings from '../hooks/useSettings';
import Icon from '../Icon';

type TimeFieldProps = Omit<FieldProps<string, Moment>, 'endAdornment'>;

const TimeTextField = styled(BaseTextField)<BaseTextFieldProps<string>>(({ isEdit, theme }) => ({
	'&.MuiTextField-root > .MuiInputBase-root': {
		paddingRight: isEdit ? 4 : 12
	}
}));

/** The time field, used to specify time */
function TimeField(props: TimeFieldProps) {
	const { sysSettings } = useSettings();
	const intl = useIntl();
	const hasLogs = props.logProps?.hasLogs(props.name);

	const ref = React.useRef<HTMLDivElement>(null);

	const minutesStep = Number.parseInt(sysSettings.min_time_interval, 10) || 15;

	const [dialogOpen, setDialogOpen] = React.useState(false);
	const switchDialogOpen = () => setDialogOpen(state => !state);

	const internalValue = React.useRef(parseDate(props.value));

	function fieldParser(fieldValue: MomentInput) {
		const parsedValue = parseDate(fieldValue)?.toDate();
		if (parsedValue) return intl.formatDate(parsedValue, { hour: '2-digit', minute: '2-digit' });
		return null;
	}

	function handleBlur() {
		const newValue = internalValue && internalValue.current?.isValid() ? internalValue.current?.utc() : undefined;
		if (props.beforeChange) props.beforeChange(newValue);
		props.sendChange(props.name, newValue?.toISOString() || null);
		if (props.afterChange) props.afterChange(newValue);
	}

	function handleChange(newValue: Moment | null) {
		internalValue.current = newValue?.year(1900).month(0).date(1).utc();
		props.setValue(internalValue.current?.toISOString() || null);
	}

	function handleDialogClose() {
		handleBlur();
		setDialogOpen(false);
	}

	const { minTime, maxTime } = props.validations || {};

	return (
		<TimePicker
			ref={ref}
			className={props.className}
			name={props.name}
			label={props.label}
			format="HH:mm"
			minTime={minTime ? moment.utc(minTime) : undefined}
			maxTime={maxTime ? moment.utc(maxTime) : undefined}
			timeSteps={{ minutes: minutesStep || 5 }}
			value={props.value ? moment.utc(props.value) : null}
			slots={{ textField: TimeTextField }}
			slotProps={{
				textField: {
					name: props.name,
					isEdit: props.isEdit,
					startAdornment: props.startAdornment,
					InputProps: {
						endAdornment: props.isEdit ? (
							<IconButton size="small" onClick={switchDialogOpen}>
								<Icon icon="clock-outline" />
							</IconButton>
						) : (
							<></>
						)
					},
					required: props.required,
					errorMessage: props.errorMessage,
					isFormSubmitted: props.isFormSubmitted,
					isPristine: props.isPristine,
					isValid: props.isValid,
					logProps: props.logProps,
					fieldParser,
					onBlur: handleBlur
				} as BaseTextFieldProps<string>,
				popper: {
					anchorEl: ref?.current
				},
				actionBar: {
					actions: []
				}
			}}
			timezone={moment.tz.guess()}
			readOnly={hasLogs}
			disabled={!props.isEdit && !hasLogs}
			onChange={handleChange}
			// onAccept={handleBlur}
			open={dialogOpen}
			onClose={handleDialogClose}
			ampm={false}
			disableIgnoringDatePartForTimeValidation={false}
			skipDisabled
			desktopModeMediaQuery=""
			enableAccessibleFieldDOMStructure={false}
		/>
	);
}

export default React.memo(withFormsy(TimeField));
